import type { Variant, Audience, SessionInfo } from '../types'
import { getCookie } from './cookies'

const FALLBACK_KEY = 'leadflow_session_v1'
const QS_OVERRIDE_KEY = 'leadflow_variant_override'

function uuid(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID()
  // RFC4122-ish fallback
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

/**
 * If the chat app is loaded directly (not through the variant-router), assign
 * variant='chat'. The router sets `lf_variant` cookie for users routed to chat,
 * so this fallback only matters for direct visits to the pages.dev URL or QA.
 */
function defaultVariant(): Variant {
  return 'chat'
}

/**
 * Audience defaults to 'unge' if neither the path-based edge cookie nor an
 * explicit override is present. Routes /unge/* and /aeldre/* in
 * functions/index.ts set `lf_audience` BEFORE the SPA loads, so this fallback
 * mainly handles direct pages.dev visits / QA.
 */
function defaultAudience(): Audience {
  return 'unge'
}

/**
 * Læs/skriv-helpers wrapper alle storage-kald i try/catch fordi privacy-mode
 * Safari, iframe-embeds med third-party-cookie-blokering, og enkelte enterprise-
 * device-policies kaster `SecurityError`/`QuotaExceededError` på allerførste
 * Storage-tilgang. Uden denne wrap'er ville hele widget'en eller calculatoren
 * crashe synligt med "Cannot read properties of undefined" eller en uncaught
 * exception, hvor en silent fallback til in-memory-state er strengt bedre.
 */
function safeSessionRead(key: string): string | null {
  try { return typeof window !== 'undefined' ? sessionStorage.getItem(key) : null }
  catch { return null }
}
function safeSessionWrite(key: string, value: string): void {
  try { if (typeof window !== 'undefined') sessionStorage.setItem(key, value) }
  catch { /* storage disabled — silently drop, widget still works in memory */ }
}
function safeLocalRead(key: string): string | null {
  try { return typeof window !== 'undefined' ? localStorage.getItem(key) : null }
  catch { return null }
}
function safeLocalWrite(key: string, value: string): void {
  try { if (typeof window !== 'undefined') localStorage.setItem(key, value) }
  catch { /* storage disabled */ }
}

/**
 * QA override via ?variant=chat|calculator. Persisted to sessionStorage so it
 * survives page navigation but not future visits.
 */
function readVariantOverride(): Variant | null {
  if (typeof window === 'undefined') return null
  const stored = safeSessionRead(QS_OVERRIDE_KEY)
  if (stored === 'chat' || stored === 'calculator') return stored
  const params = new URLSearchParams(window.location.search)
  const v = params.get('variant')
  if (v === 'chat' || v === 'calculator') {
    safeSessionWrite(QS_OVERRIDE_KEY, v)
    return v
  }
  return null
}

/**
 * Returns SessionInfo synchronously from cookies/localStorage so the first paint
 * uses the correct theme. The server (`POST /api/session`) reconciles authoritatively
 * and may overwrite the cookie if it disagrees, but the offline-first behavior keeps
 * the widget usable even if the API is briefly unavailable.
 */
export function readSessionFromClient(): SessionInfo {
  const override = readVariantOverride()

  let sessionId = getCookie('lf_session') ?? safeLocalRead(FALLBACK_KEY)
  if (!sessionId) {
    sessionId = uuid()
    safeLocalWrite(FALLBACK_KEY, sessionId)
  }

  const cookieVariant = getCookie('lf_variant') as Variant | null
  let variant: Variant
  if (override) variant = override
  else if (cookieVariant === 'chat' || cookieVariant === 'calculator') variant = cookieVariant
  else variant = defaultVariant()

  const cookieAudience = getCookie('lf_audience') as Audience | null
  const audience: Audience =
    cookieAudience === 'unge' || cookieAudience === 'aeldre' ? cookieAudience : defaultAudience()

  return { sessionId, variant, audience }
}
